import { useState, useMemo } from "react";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Popup,
  Tooltip,
  Circle
} from "react-leaflet";
import L from "leaflet";
import { motion } from "framer-motion";
import { useMunicipalStore } from "../../store/municipalStore";
import Button from "../shared/Button";
import { Header, Container } from "../shared/Layout";
import { MapPin, Filter } from "lucide-react";

delete L.Icon.Default.prototype._getIconUrl;

const stateColors = {
  enviado: "#0066cc",
  clasificado: "#6f42c1",
  asignado: "#ffd700",
  en_proceso: "#ff9800",
  resuelto: "#28a745"
};

const priorityColors = {
  baja: "#2ecc71",
  media: "#f1c40f",
  alta: "#e74c3c",
};

export default function MapaTerritorial() {

  const requests = useMunicipalStore(state => state.requests);
  const areas = useMunicipalStore(state => state.areas);

  const [filterState, setFilterState] = useState("todos");
  const [filterArea, setFilterArea] = useState("todos");

  const filteredRequests = useMemo(() => {
    return requests.filter(r => {
      const stateMatch = filterState === "todos" || r.estado === filterState;
      const areaMatch = filterArea === "todos" || r.area === filterArea;
      return stateMatch && areaMatch;
    });
  }, [requests, filterState, filterArea]);

  const centerLat =
    filteredRequests.length > 0
      ? filteredRequests.reduce((sum, r) => sum + r.lat, 0) /
        filteredRequests.length
      : -17.893625;

  const centerLng =
    filteredRequests.length > 0
      ? filteredRequests.reduce((sum, r) => sum + r.lng, 0) /
        filteredRequests.length
      : -63.327754;

  /*
  ==========================
  DETECTOR DE ZONAS CRITICAS
  ==========================
  */

  const problemZones = useMemo(() => {
    const zones = {};

    filteredRequests.forEach(r => {
      if(r.estado == "resuelto") return;

      const key =
        `${Math.round(r.lat * 100) / 100},${Math.round(r.lng * 100) / 100}`;

      if (!zones[key]) {
        zones[key] = {
          count: 0,
          lat: Math.round(r.lat * 100) / 100,
          lng: Math.round(r.lng * 100) / 100
        };
      }

      zones[key].count++;
    });

    return Object.values(zones);
  }, [filteredRequests]);

  const getZoneColor = (count) => {

    if (count >= 10) return "#ff0000";
    if (count >= 6) return "#ff6b6b";
    if (count >= 3) return "#ffa500";

    return "#ffd166";
  };

  return (
    <Container>

      <Header
        title="Mapa Territorial Inteligente"
        subtitle="Análisis geográfico de requerimientos ciudadanos"
        icon={MapPin}
      />

      {/* FILTROS */}

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex flex-wrap gap-4 mb-6"
      >

        <div className="flex-1 min-w-[200px]">

          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Estado
          </label>

          <select
            value={filterState}
            onChange={(e) => setFilterState(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg"
          >
            <option value="todos">Todos</option>
            <option value="enviado">Recibidas</option>
            <option value="en_proceso">En proceso</option>
            <option value="resuelto">Resueltas</option>
          </select>

        </div>

        <div className="flex-1 min-w-[200px]">

          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Área municipal
          </label>

          <select
            value={filterArea}
            onChange={(e) => setFilterArea(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg"
          >

            <option value="todos">Todas</option>

            {areas.map(area => (
              <option key={area.id} value={area.name}>
                {area.name}
              </option>
            ))}

          </select>

        </div>

        <div className="flex items-end">
          <Button variant="primary">
            <Filter size={18} />
            Filtrar
          </Button>
        </div>

      </motion.div>

      {/* MAPA */}

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="rounded-2xl overflow-hidden shadow-lg border h-[650px]"
      >

        <MapContainer
          center={[centerLat, centerLng]}
          zoom={13}
          style={{ height: "100%" }}
        >

          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {/* ZONAS CRITICAS */}

          {problemZones.map((zone, idx) => (

            <Circle
              key={idx}
              center={[zone.lat, zone.lng]}
              radius={zone.count * 60}
              pathOptions={{
                color: getZoneColor(zone.count),
                fillColor: getZoneColor(zone.count),
                fillOpacity: 0.25
              }}
            >

              <Tooltip direction="top">

                Zona con {zone.count} requerimientos

              </Tooltip>

              <Popup>

                <div className="text-sm">

                  <b>Zona crítica detectada</b>

                  <p>{zone.count} solicitudes en esta área</p>

                  <p>
                    Priorizar atención municipal en este sector
                  </p>

                </div>

              </Popup>

            </Circle>

          ))}

          {/* REQUERIMIENTOS */}

          {filteredRequests.map(req => (

            <CircleMarker
              key={req.id}
              center={[req.lat, req.lng]}
              radius={8}
              pathOptions={{
                color: req.estado == "resuelto" ? "#3498db" : priorityColors[req.urgencia] || "#3498db",
                fillColor: req.estado == "resuelto" ? "#3498db" : priorityColors[req.urgencia] || "#3498db",
                fillOpacity: 0.9
              }}
            >

              <Tooltip>

                {req.tipo}

              </Tooltip>

              <Popup>

                <div className="text-sm space-y-1">

                  <b>{req.tipo}</b>

                  <p><b>Barrio:</b> {req.barrio}</p>

                  <p><b>Área:</b> {req.area}</p>

                  <p>
                    <b>Estado:</b>
                    <span
                      className="ml-2 px-2 py-1 text-xs text-white rounded"
                      style={{
                        background: stateColors[req.estado]
                      }}
                    >
                      {req.estado}
                    </span>
                  </p>

                  <p>
                    <b>Prioridad:</b>
                    <span
                      className="ml-2 px-2 py-1 text-xs text-white rounded"
                      style={{
                        background: priorityColors[req.urgencia]
                      }}
                    >
                      {req.urgencia}
                    </span>
                  </p>

                </div>

              </Popup>

            </CircleMarker>

          ))}

        </MapContainer>

      </motion.div>

      {/* LEYENDA */}

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="grid md:grid-cols-4 gap-4 mt-6"
      >

        <div className="bg-white border rounded-xl p-4">
          <p className="text-gray-600 text-sm">
            Solicitudes en mapa
          </p>
          <p className="text-3xl font-bold text-blue-600">
            {filteredRequests.length}
          </p>
        </div>

        <div className="bg-white border rounded-xl p-4">
          <p className="text-gray-600 text-sm">
            Zonas críticas
          </p>
          <p className="text-3xl font-bold text-red-600">
            {problemZones.length}
          </p>
        </div>

        <div className="bg-white border rounded-xl p-4">
          <p className="text-gray-600 text-sm">
            En proceso
          </p>
          <p className="text-3xl font-bold text-orange-500">
            {
              filteredRequests.filter(
                r => r.estado === "en_proceso"
              ).length
            }
          </p>
        </div>

        <div className="bg-white border rounded-xl p-4">
          <p className="text-gray-600 text-sm">
            Resueltas
          </p>
          <p className="text-3xl font-bold text-green-600">
            {
              filteredRequests.filter(
                r => r.estado === "resuelto"
              ).length
            }
          </p>
        </div>

      </motion.div>

    </Container>
  );
}